/**
 * Handoff de sessão dos apps (desktop Vision, from=vision; mobile, from=app) para a web.
 *
 * O app já tem os tokens do usuário e abre o navegador numa rota autenticada
 * (ex.: /profile/ai?from=vision&buy=1, ou ?buy=2 para a modal de planos). Para o
 * AuthGuard não jogar o usuário pro /login, o app pede à API um código de uso único e o
 * passa na URL:
 *
 *   https://dev.ftpilot.com/profile/ai?from=vision&buy=1&hc=<código>
 *
 * O código vive 60s, morre na primeira troca e é trocado pelos tokens num
 * POST /global/handoff/exchange (AuthService.consumeHandoffCode). Assim **nenhum token
 * aparece em URL** — nem na query,
 * nem no fragmento, nem na linha de comando com que o Vision abre o navegador.
 *
 * Contrato: ftcloud-api/docs/integracoes/HANDOFF_DESKTOP_WEB_ONETIME_CODE.md
 *
 * Este arquivo só manipula a URL. A troca e a instalação da sessão ficam no AuthService,
 * que já é o dono do ciclo de vida da sessão.
 */

/** Nome do parâmetro de query que carrega o código de uso único. */
const HANDOFF_PARAM = 'hc';

/**
 * Lê o código de handoff da query string e o remove da URL, junto com qualquer fragmento.
 *
 * Roda no APP_INITIALIZER, antes de o Router inicializar — então o replaceState não
 * dessincroniza o estado do Angular.
 *
 * O fragmento é limpo sempre, mesmo sem código: este app não usa fragmento para navegação
 * (nem hash routing, nem anchorScrolling), então não há nada legítimo ali. Isso descarta
 * resíduo de uma build antiga do Vision que ainda mande tokens no fragmento — que não são
 * mais lidos, e não devem ficar na barra de endereços nem no histórico.
 *
 * @returns o código, ou null se não havia.
 */
export function readAndStripHandoffCode(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get(HANDOFF_PARAM);
    const hadFragment = window.location.hash.length > 0;

    if (!code && !hadFragment) return null;

    if (code) params.delete(HANDOFF_PARAM);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? `?${query}` : ''),
    );

    return code || null;
  } catch {
    return null;
  }
}

/**
 * Remove o fragmento de uma URL.
 *
 * Usado no scrubbing da Sentry (main.ts) e no setPostLoginRedirect. Mantido mesmo agora que
 * o handoff não usa mais o fragmento: a Sentry roda com sendDefaultPii: true, e o
 * postLoginRedirect grava a URL em localStorage sem prazo — nenhum dos dois deve receber
 * fragmento, seja de onde vier.
 */
export function stripUrlFragment<T>(url: T): T {
  if (typeof url !== 'string') return url;
  const i = url.indexOf('#');
  return (i === -1 ? url : url.slice(0, i)) as unknown as T;
}
